import React from 'react';
import { Source, Layer as MapLibreLayer } from 'react-map-gl/maplibre';
import { METHOD_COLORS, toFeatureCollection } from '../../lib/gis-types';
import { MAP_FONT_STACK, MAP_FONT_STACK_BOLD } from '../../lib/map-styles';
import { AI_SUGGESTED_LINE } from '../../lib/mapViewHelpers';
import { layerTileUrl } from '../../hooks/useRasterLayers';
import { pointKind, symbolSpec, heatmapPaint, resolveLabels, labelLayerSpec } from '../../lib/layer-style';
import PointSymbolImages from './PointSymbolImages';
import AnnotationEditor from './AnnotationEditor';

/**
 * react-map-gl's Layer passes every prop to `map.addLayer`, and MapLibre rejects keys set to
 * `undefined` ("filter: array expected, undefined found"). Drop them, so `filter={maybe}`,
 * `minzoom={maybe}` etc. are safe everywhere in this file.
 */
export function definedProps(props) {
  const out = {};
  Object.keys(props).forEach((k) => { if (props[k] !== undefined) out[k] = props[k]; });
  return out;
}

function Layer(props) {
  return <MapLibreLayer {...definedProps(props)} />;
}

/**
 * A point layer under its style: circle (default), SDF symbol (square / triangle) or
 * heatmap. The `key` remounts the layer when the render kind changes, since a MapLibre
 * layer cannot change type in place.
 * `<Source>` injects `source` (and a vector source may need `source-layer`) into its direct
 * children: wrappers must hand those on to the inner Layer, hence `...injected`.
 */
// maplibre-gl 5 keeps a layer's tile buckets by id across a remove/add, so switching a point layer between
// circle / symbol / heatmap throws in the heatmap draw; each Source key therefore carries pointKind to
// rebuild its tiles when the kind changes.
function PointLayer({ id, chartId, layerStyles, stylePaint, defaults, filter, ...injected }) {
  const s = layerStyles[chartId];
  const kind = pointKind(s);
  if (kind === 'symbol') {
    const { layout, paint } = symbolSpec(s);
    return <Layer key="symbol" {...injected} id={id} type="symbol" layout={layout} paint={paint} filter={filter} />;
  }
  if (kind === 'heatmap') return <Layer key="heatmap" {...injected} id={id} type="heatmap" paint={heatmapPaint(s)} filter={filter} />;
  return <Layer key="circle" {...injected} id={id} type="circle" paint={stylePaint(chartId, 'circle', defaults)} filter={filter} />;
}

/** The single symbol sub-layer of a labelled layer (style.labels, lib/layer-style.js). */
function LabelLayer({ id, chartId, layerStyles, withChartFilter, ...injected }) {
  const labels = resolveLabels(layerStyles[chartId]);
  if (!labels) return null;
  const spec = labelLayerSpec(labels);
  return <Layer {...injected} id={id} type="symbol" layout={spec.layout} paint={spec.paint} minzoom={spec.minzoom}
    filter={withChartFilter(chartId, spec.geomType)} />;
}

export default function MapDataLayers({ rasterLayers = [], showHeatmap, usgsData, dbFeatures, demoEnabled = true, layers, importedLayers, resultLayers, layerStyles, xyzPaint, stylePaint, withChartFilter, lineData, electrodeData, soundingData, anomalyData, lineLabelData, annotations, annotationData, annotationEdit, saveAnnotation, removeAnnotation, cancelAnnotationEdit, measurePoints, measureDone, measureGeoJSON, serviceLayers }) {
  return (
    <>
      <PointSymbolImages />
      {/* Raster datasets (hooks/useRasterLayers): first, so vector layers draw on top.
          The source id carries the style revision: a new colormap/range is a new source. */}
      {rasterLayers.filter(r => r.visible).map(r => (
        <Source key={`${r.id}-${r.rev}`} id={`${r.id}-src-${r.rev}`} type="raster" tiles={[layerTileUrl(r)]}
          tileSize={256} {...(r.bounds ? { bounds: [...r.bounds[0], ...r.bounds[1]] } : {})}>
          <Layer id={`${r.id}-layer-${r.rev}`} type="raster" paint={{ 'raster-opacity': r.opacity, 'raster-fade-duration': 0 }} />
        </Source>
      ))}
      {/* DEM contours (docs/DEM_TERRAIN.md): index contours (every 5th) heavier */}
      {rasterLayers.filter(r => r.visible && r.showContours && r.contours).map(r => (
        <Source key={`${r.id}-contours`} id={`${r.id}-contours-src`} type="geojson" data={r.contours}>
          <Layer id={`${r.id}-contours`} type="line" paint={{
            'line-color': '#6b4423', 'line-opacity': 0.85,
            'line-width': ['case', ['==', ['get', 'index'], true], 1.6, 0.7],
          }} />
        </Source>
      ))}

      {/* Survey lines */}
      <Source id="survey-lines-src" type="geojson" data={lineData}>
        <Layer id="survey-lines" type="line" paint={{
          'line-color': ['get', 'color'],
          'line-width': 3,
          'line-opacity': 0.9,
        }} />
      </Source>

      {/* Electrode dots */}
      <Source id="electrodes-src" type="geojson" data={electrodeData}>
        <Layer id="electrodes" type="circle" paint={{
          'circle-radius': 2.5,
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.6,
        }} minzoom={13} />
      </Source>

      {/* Line labels */}
      <Source id="line-labels-src" type="geojson" data={lineLabelData}>
        <Layer id="line-labels" type="symbol" layout={{
          'text-field': ['get', 'name'],
          'text-font': MAP_FONT_STACK, 'text-size': 10,
          'text-anchor': 'right',
          'text-offset': [-0.5, 0],
        }} paint={{
          'text-color': ['get', 'color'],
          'text-opacity': 0.8,
        }} />
      </Source>

      {/* Soundings */}
      <Source id="soundings-src" type="geojson" data={soundingData}>
        <Layer id="soundings" type="circle" paint={{
          'circle-radius': 7,
          'circle-color': ['get', 'color'],
          'circle-opacity': showHeatmap ? 0.3 : 0.9,
          'circle-stroke-color': '#000',
          'circle-stroke-width': 0.5,
        }} />
        <Layer id="sounding-labels" type="symbol" layout={{
          'text-field': ['get', 'label'],
          'text-font': MAP_FONT_STACK, 'text-size': 9,
          'text-offset': [1.2, 0],
          'text-anchor': 'left',
        }} paint={{
          'text-color': ['get', 'color'],
          'text-opacity': 0.9,
        }} />
        {/* Heatmap layer */}
        {showHeatmap && (
          <Layer id="sounding-heatmap" type="heatmap" paint={{
            'heatmap-radius': 40,
            'heatmap-opacity': 0.7,
            'heatmap-intensity': 1.5,
            'heatmap-color': [
              'interpolate', ['linear'], ['heatmap-density'],
              0, 'rgba(0,0,0,0)',
              0.2, '#2563eb',
              0.4, '#00b8de',
              0.6, '#34d399',
              0.8, '#fbbf24',
              1.0, '#f87171',
            ],
          }} beforeId="soundings" />
        )}
      </Source>

      {/* Anomalies — dashed circles */}
      {demoEnabled && <Source id="anomalies-src" type="geojson" data={anomalyData}>
        <Layer id="anomaly-circles" type="circle" paint={{
          'circle-radius': 18,
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.12,
          'circle-stroke-color': ['get', 'color'],
          'circle-stroke-width': 1.5,
          'circle-stroke-opacity': 0.8,
        }} />
        <Layer id="anomaly-labels" type="symbol" layout={{
          'text-field': ['get', 'label'],
          'text-size': 10,
          'text-font': MAP_FONT_STACK_BOLD,
        }} paint={{
          'text-color': ['get', 'color'],
        }} />
      </Source>}

      {/* AI suggested line — dashed purple */}
      {demoEnabled && <Source id="ai-line-src" type="geojson" data={toFeatureCollection([AI_SUGGESTED_LINE])}>
        <Layer id="ai-line" type="line" paint={{
          'line-color': '#a78bfa',
          'line-width': 1.5,
          'line-opacity': 0.6,
          'line-dasharray': [6, 4],
        }} />
        <Layer id="ai-line-label" type="symbol" layout={{
          'text-field': 'AI: suggest L-09',
          'text-font': MAP_FONT_STACK, 'text-size': 10,
          'symbol-placement': 'line',
          'text-offset': [0, -1],
        }} paint={{
          'text-color': '#a78bfa',
          'text-opacity': 0.8,
        }} />
      </Source>}

      {/* Annotations */}
      <Source id="annotations-src" type="geojson" data={annotationData}>
        <Layer id="annotation-icons" type="circle" paint={{
          'circle-radius': 6,
          'circle-color': '#fbbf24',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1.5,
        }} />
        <Layer id="annotation-labels" type="symbol" layout={{
          'text-field': ['concat', '📌 ', ['get', 'text']],
          'text-font': MAP_FONT_STACK, 'text-size': 11,
          'text-anchor': 'left',
          'text-offset': [1, 0],
        }} paint={{
          'text-color': '#fbbf24',
          'text-halo-color': '#0d1117',
          'text-halo-width': 1,
        }} />
      </Source>

      <AnnotationEditor edit={annotationEdit} onSave={saveAnnotation} onDelete={removeAnnotation} onCancel={cancelAnnotationEdit} />

      {/* Analysis result layers */}
      {resultLayers.filter(r => r.visible).map(r => (
        <Source key={`${r.id}-${pointKind(layerStyles[r.id])}`} id={`result-${r.id}`} type="geojson" data={r.geojson}>
          {r.geojson.features?.[0]?.geometry?.type === 'Point' ? (
            <PointLayer id={`result-pts-${r.id}`} chartId={r.id} layerStyles={layerStyles} stylePaint={stylePaint}
              filter={withChartFilter(r.id, 'Point')}
              defaults={r.paint ? r.paint : r.kind === 'grid' ? {
                // Interpolated surface — gradient by normalized value
                'circle-radius': 4,
                'circle-color': ['interpolate', ['linear'], ['to-number', ['get', 'normalized'], 0],
                  0, '#2563eb', 0.25, '#00b8de', 0.5, '#34d399', 0.75, '#fbbf24', 1, '#f87171'],
                'circle-opacity': 0.85,
                'circle-stroke-width': 0,
              } : {
                // Vector/categorical results — honor per-feature color (LISA, clusters)
                'circle-radius': 5,
                'circle-color': ['coalesce', ['get', 'color'], '#fbbf24'],
                'circle-opacity': 0.85,
                'circle-stroke-color': '#fff', 'circle-stroke-width': 1,
              }} />
          ) : r.kind === 'contour' ? [
            // An array, not a Fragment: <Source> injects `source` into its direct children only.
            /* Filled contour bands */
            <Layer key="fill" id={`result-fill-${r.id}`} type="fill" paint={{
                'fill-color': ['coalesce', ['get', 'color'], '#00d4ff'],
                'fill-opacity': 0.45,
              }} filter={['==', ['geometry-type'], 'Polygon']} />,
            /* Isolines */
            <Layer key="line" id={`result-line-${r.id}`} type="line" paint={{
                'line-color': '#0b1220', 'line-width': 0.8, 'line-opacity': 0.65,
              }} filter={['==', ['geometry-type'], 'LineString']} />,
            /* Contour value labels */
            <Layer key="clabel" id={`result-clabel-${r.id}`} type="symbol" layout={{
                'symbol-placement': 'line',
                'text-field': ['to-string', ['get', 'value']],
                'text-font': MAP_FONT_STACK, 'text-size': 10,
                'symbol-spacing': 300,
              }} paint={{
                'text-color': '#e6edf3', 'text-halo-color': '#0d1117', 'text-halo-width': 1.2,
              }} filter={['==', ['geometry-type'], 'LineString']} />,
          ] : [
            <Layer key="fill" id={`result-fill-${r.id}`} type="fill" paint={stylePaint(r.id, 'fill', {
                'fill-color': '#00d4ff', 'fill-opacity': 0.15,
              })} filter={withChartFilter(r.id, 'Polygon')} />,
            <Layer key="line" id={`result-line-${r.id}`} type="line" paint={stylePaint(r.id, 'line', {
                'line-color': '#00d4ff', 'line-width': 2, 'line-opacity': 0.8,
              })} filter={withChartFilter(r.id, null)} />,
          ]}
          {r.kind !== 'contour' && <LabelLayer id={`result-label-${r.id}`} chartId={r.id} layerStyles={layerStyles} withChartFilter={withChartFilter} />}
        </Source>
      ))}

      {/* External service layers */}
      {serviceLayers.filter(s => s.visible).map(s => (
        s.type === 'raster' ? (
          <Source key={s.id} id={s.id} type="raster" tiles={[s.tileUrl]} tileSize={256}>
            <Layer id={`layer-${s.id}`} type="raster" paint={{ 'raster-opacity': 0.7 }} />
          </Source>
        ) : s.type === 'vector' && s.geojson ? (
          <Source key={s.id} id={s.id} type="geojson" data={s.geojson}>
            <Layer id={`fill-${s.id}`} type="fill" paint={{ 'fill-color': '#00b8de', 'fill-opacity': 0.15 }} filter={['==', '$type', 'Polygon']} />
            <Layer id={`line-${s.id}`} type="line" paint={{ 'line-color': '#00b8de', 'line-width': 1.5 }} />
            <Layer id={`pts-${s.id}`} type="circle" paint={{ 'circle-radius': 4, 'circle-color': '#00b8de', 'circle-opacity': 0.8 }} filter={['==', '$type', 'Point']} />
          </Source>
        ) : null
      ))}

      {/* USGS real data layers */}
      {Object.entries(usgsData).map(([layerId, geojson]) => {
        const layer = layers.find(l => l.id === layerId);
        if (!layer?.visible || !geojson?.features) return null;
        const color = METHOD_COLORS[layer.method] || '#00d4ff';
        return (
          <Source key={`usgs-${layerId}-${pointKind(layerStyles[`usgs-${layerId}`])}`} id={`usgs-${layerId}`} type="geojson" data={geojson}>
            <Layer id={`usgs-line-${layerId}`} type="line" paint={stylePaint(`usgs-${layerId}`, 'line', { 'line-color': color, 'line-width': 1.5, 'line-opacity': layer.opacity / 100 })} filter={withChartFilter(`usgs-${layerId}`, 'LineString')} />
            <PointLayer id={`usgs-pts-${layerId}`} chartId={`usgs-${layerId}`} layerStyles={layerStyles} stylePaint={stylePaint}
              defaults={{ 'circle-radius': 5, 'circle-color': color, 'circle-opacity': layer.opacity / 100, 'circle-stroke-color': '#fff', 'circle-stroke-width': 1 }}
              filter={withChartFilter(`usgs-${layerId}`, 'Point')} />
            <Layer id={`usgs-fill-${layerId}`} type="fill" paint={stylePaint(`usgs-${layerId}`, 'fill', { 'fill-color': color, 'fill-opacity': 0.1 })} filter={withChartFilter(`usgs-${layerId}`, 'Polygon')} />
            <LabelLayer id={`usgs-label-${layerId}`} chartId={`usgs-${layerId}`} layerStyles={layerStyles} withChartFilter={withChartFilter} />
          </Source>
        );
      })}

      {/* Imported file layers */}
      {importedLayers.filter(l => l.visible).map(l => (
        <Source key={`${l.id}-${pointKind(layerStyles[l.id])}`} id={l.id} type="geojson" data={l.geojson}>
          <Layer id={`imp-fill-${l.id}`} type="fill" paint={stylePaint(l.id, 'fill', { 'fill-color': '#a78bfa', 'fill-opacity': 0.2 })} filter={withChartFilter(l.id, 'Polygon')} />
          <Layer id={`imp-line-${l.id}`} type="line" paint={stylePaint(l.id, 'line', { 'line-color': '#a78bfa', 'line-width': 2 })} filter={withChartFilter(l.id, null)} />
          <PointLayer id={`imp-pts-${l.id}`} chartId={l.id} layerStyles={layerStyles} stylePaint={stylePaint}
            defaults={xyzPaint(l) || { 'circle-radius': 5, 'circle-color': '#a78bfa', 'circle-opacity': 0.9, 'circle-stroke-color': '#fff', 'circle-stroke-width': 1 }}
            filter={l.xyz
              ? ['all', ['==', ['geometry-type'], 'Point'], ['!', ['coalesce', ['get', '_excluded'], false]], ...(withChartFilter(l.id, null) ? [withChartFilter(l.id, null)] : [])]
              : withChartFilter(l.id, 'Point')} />
          {l.xyz && (
            <Layer id={`imp-excl-${l.id}`} type="circle" paint={{
              'circle-radius': 4, 'circle-color': '#484f58', 'circle-opacity': 0.6,
              'circle-stroke-color': '#8b949e', 'circle-stroke-width': 1,
            }} filter={['all', ['==', ['geometry-type'], 'Point'], ['coalesce', ['get', '_excluded'], false]]} />
          )}
          <LabelLayer id={`imp-label-${l.id}`} chartId={l.id} layerStyles={layerStyles} withChartFilter={withChartFilter} />
        </Source>
      ))}

      {/* DB features from viewport loading — MapLibre NATIVE clustering */}
      {dbFeatures?.points?.features?.length > 0 && (
        <Source id="db-features-pts" type="geojson" data={dbFeatures.points}
          cluster={true} clusterRadius={50} clusterMaxZoom={12}>
          {/* Cluster bubbles, sized/colored by count */}
          <Layer id="db-clusters" type="circle" filter={['has', 'point_count']} paint={{
            'circle-radius': ['step', ['get', 'point_count'], 14, 25, 20, 100, 26],
            'circle-color': ['step', ['get', 'point_count'], '#00d4ff', 25, '#fbbf24', 100, '#f87171'],
            'circle-opacity': 0.75,
            'circle-stroke-color': '#0b1220', 'circle-stroke-width': 1.5,
          }} />
          <Layer id="db-cluster-count" type="symbol" filter={['has', 'point_count']} layout={{
            'text-field': ['get', 'point_count_abbreviated'], 'text-font': MAP_FONT_STACK, 'text-size': 11,
          }} paint={{ 'text-color': '#0b1220' }} />
          {/* Unclustered points, colored by feature type */}
          <Layer id="db-points" type="circle" filter={['!', ['has', 'point_count']]} paint={{
            'circle-radius': 5,
            'circle-color': ['match', ['get', 'feature_type'],
              'sounding', '#00d4ff', 'anomaly', '#f87171', 'dataset', '#a78bfa', '#34d399'],
            'circle-opacity': 0.9,
            'circle-stroke-color': '#fff', 'circle-stroke-width': 1,
          }} />
        </Source>
      )}
      {dbFeatures?.lines?.features?.length > 0 && (
        <Source id="db-features-lines" type="geojson" data={dbFeatures.lines}>
          <Layer id="db-lines" type="line" paint={{
            'line-color': '#34d399', 'line-width': 2, 'line-opacity': 0.8,
          }} />
        </Source>
      )}

      {/* Drawn features are rendered by Terra Draw's own layers */}

      {/* Measurement line + points */}
      {measurePoints.length > 0 && (
        <Source id="measure-src" type="geojson" data={measureGeoJSON}>
          <Layer id="measure-line" type="line" paint={{
            'line-color': '#fbbf24', 'line-width': 2.5,
            'line-dasharray': measureDone ? [1, 0] : [4, 3],
          }} />
          <Layer id="measure-pts" type="circle" paint={{
            'circle-radius': 4.5, 'circle-color': '#fbbf24',
            'circle-stroke-color': '#0d1117', 'circle-stroke-width': 1.5,
          }} filter={['==', '$type', 'Point']} />
        </Source>
      )}
    </>
  );
}
